import { addToast } from "components/ToastContainer/ToastStore";
import {
    ChangeEvent,
    FormEvent,
    useCallback,
    useEffect,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useSignUpMutation } from "slice/userAuth";
import { validations } from "utils/validations";
import { ActiveStep, UserType } from "./types";

export const useSignUp = () => {
    const [register, { isLoading, isSuccess, error }] = useSignUpMutation();
    const navigateTo = useNavigate();
    const [activeStep, setActiveStep] = useState<ActiveStep>(1);
    const [userType, setUserType] = useState<UserType>("talent");

    const [inputs, setInputs] = useState({
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            passwordConfirmation: "",
            isTermsAndConditionsAgree: false,
        }),
        [inputErrors, setInputErrors] = useState({
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            passwordConfirmation: "",
            isTermsAndConditionsAgree: "",
        });

    const getConfirmPasswordError = useCallback(
        (val: string) => {
            let error = "";
            if (val === "") {
                error = "Please enter a valid password.";
            } else if (val !== inputs.password) {
                error =
                    "Please make sure that password and confirm password are same.";
            }

            return error;
        },
        [inputs.password]
    );

    const handleInputChange = useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                const { name, value } = e.target;
                let error = "";
                if (name === "passwordConfirmation") {
                    error = getConfirmPasswordError(value);
                } else {
                    error = validations(name, value);
                }
                setInputs((prev) => ({
                    ...prev,
                    [name]: value,
                }));

                setInputErrors((prev) => ({
                    ...prev,
                    [name]: error,
                }));
            },
            [getConfirmPasswordError]
        ),
        toggleCheckbox = () => {
            setInputs((prev) => ({
                ...prev,
                isTermsAndConditionsAgree: !prev.isTermsAndConditionsAgree,
            }));

            setInputErrors((prev) => ({
                ...prev,
                isTermsAndConditionsAgree: prev.isTermsAndConditionsAgree
                    ? "This field is required"
                    : "",
            }));
        },
        handleGoBack = () => {
            setActiveStep(1);
        },
        handleUserTypeChange = (userType: UserType) => {
            setUserType(userType);
        },
        checkForErrors = useCallback(() => {
            let flag = true;
            // let newErrors = inputErrors;
            Object.keys(inputs).forEach((key) => {
                let error = "";
                if (key === "passwordConfirmation") {
                    error = getConfirmPasswordError(inputs[key]);
                } else {
                    error = validations(
                        key,
                        inputs[key as keyof typeof inputErrors]
                    );
                }
                if (error !== "") {
                    flag = false;
                }
                setInputErrors((prev) => ({
                    ...prev,
                    [key]: error,
                }));
                // newErrors[key as keyof typeof inputs] = error;
            });
            // setInputErrors(newErrors);

            return flag;
        }, [getConfirmPasswordError, inputs]),
        onSubmit = useCallback(
            (e: FormEvent) => {
                e.preventDefault();
                if (checkForErrors()) {
                    setActiveStep(2);
                }
            },
            [checkForErrors]
        ),
        onFinalSubmit = useCallback(() => {
            const data = {
                ...inputs,
                first_name: inputs.firstName,
                last_name: inputs.lastName,
                password_confirmation: inputs.passwordConfirmation,
            };
            if (userType === "agency") {
                register({
                    ...data,
                    role: "agency",
                });
            } else {
                register(data);
            }
        }, [inputs, register, userType]);

    useEffect(() => {
        if (isSuccess) {
            addToast({
                type: "success",
                text: "Success! A confirmation link has been sent to your email id.",
            });
            navigateTo("/login");
        }
    }, [isSuccess, navigateTo]);

    useEffect(() => {
        if (error && Object.keys(error).length > 0) {
            addToast({
                type: "error",
                text:
                    (error as any).data.detail ||
                    "Something went wrong. Please try again later.",
            });
        }
    }, [error]);

    return {
        inputs,
        handleUserTypeChange,
        userType,
        activeStep,
        onSubmit,
        handleInputChange,
        isLoading,
        isSuccess,
        inputErrors,
        toggleCheckbox,
        onFinalSubmit,
        handleGoBack,
    };
};
